import { Mail, Paperclip } from 'lucide-react';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import type { OutreachPreviewCardData, OutreachPreviewEntryData, OutreachPreviewGroupData } from '../../../../lib/outreach-preview-card';
import { WhatsAppColorGlyph, GmailGlyph, SmsGlyph } from '../../connectors/BrandGlyphs';
import { SymphonyMark } from '../../../SymphonyMark';
import { Badge, Card, ScrollArea } from '../../ui';
import styles from './OutreachPreviewCard.module.css';

/**
 * The outreach preview, rendered UNDER an assistant reply that carries `metadata.outreachPreviewCard`:
 * what each contact will receive, drawn for the channel the message travels on. A sibling of
 * `OpportunityCard` and `ConnectorConnectCard`, riding its reply the same way.
 *
 * One ROW per channel, one card per recipient in that row, so three WhatsApp messages read as three
 * green cards side by side and the one email sits on its own row. Each card is a DS `<Card>` tinted by
 * its channel (the tint, the glyph in the 40px plate) and shows the message as it lands: the fixed
 * WhatsApp intro frame around the user's words when a template fires, the SMS intro that goes first and
 * the message that follows, the from-address and subject of an email, the in-app bubble for a Symphony
 * user.
 *
 * **Every word is the server's**, captions included, in the user's language; this file has no i18n
 * keys. **It renders no buttons.** Its actions (Send, Edit, Change channel, Introduce first) are the
 * reply's ordinary choice chips, minted from the same card, so they appear just below via the normal
 * chips path.
 */
export function OutreachPreviewCard({ card }: { card: OutreachPreviewCardData }) {
  const seenRef = useItemSeenBi('outreach_preview_card', {
    additionalFields: {
      channels: card.groups.map((group) => group.channel).join(','),
      recipients: String(card.groups.reduce((n, g) => n + g.entries.length, 0)),
    },
  });
  return (
    <div ref={seenRef} className={styles.root} data-testid="chat-outreach-preview-card">
      {card.groups.map((group) => (
        <ScrollArea
          key={group.channel}
          axis="horizontal"
          className={styles.row}
          data-channel={group.channel}
          role="group"
          aria-label={group.channelLabel}
        >
          {group.entries.map((entry, index) => (
            <PreviewCard key={`${group.channel}-${index}`} group={group} entry={entry} labels={card.labels} />
          ))}
        </ScrollArea>
      ))}
    </div>
  );
}

function ChannelGlyph({ channel }: { channel: OutreachPreviewGroupData['channel'] }) {
  switch (channel) {
    case 'whatsapp':
      return <WhatsAppColorGlyph size={22} />;
    case 'sms':
      return <SmsGlyph size={22} />;
    case 'gmail':
      return <GmailGlyph size={22} />;
    case 'app':
      // Symphony delivers this one, whichever agent the reader talks to, so the tile is the brand's.
      return <SymphonyMark size={40} />;
    case 'email':
    default:
      return <Mail size={20} strokeWidth={2} aria-hidden="true" />;
  }
}

function PreviewCard({
  group,
  entry,
  labels,
}: {
  group: OutreachPreviewGroupData;
  entry: OutreachPreviewEntryData;
  labels: OutreachPreviewCardData['labels'];
}) {
  const isMail = group.channel === 'email' || group.channel === 'gmail';
  return (
    <Card component="article" variant="sunken" size="sm" className={`${styles.card} ${styles[group.channel]}`} aria-label={entry.name}>
      <header className={styles.head}>
        {/* The Symphony mark is its own dark tile; every other channel sits in the plate. */}
        {group.channel === 'app' ? (
          <ChannelGlyph channel={group.channel} />
        ) : (
          <span className={styles.tile}>
            <ChannelGlyph channel={group.channel} />
          </span>
        )}
        <div className={styles.who}>
          <div className={styles.nameRow}>
            <span className={styles.name} dir="auto">
              {entry.name}
            </span>
            {entry.symphonyUser && <Badge variant="agents" size="tiny" label={labels.badge} />}
            {entry.relation && (
              <span className={styles.relation} dir="auto">
                {entry.relation}
              </span>
            )}
          </div>
          <div className={styles.address} dir="ltr">
            <span className={styles.channelLabel}>{group.channelLabel}</span>
            {entry.address && <span className={styles.addressValue}>{entry.address}</span>}
          </div>
        </div>
      </header>
      <p className={styles.state}>{entry.stateLine}</p>
      {isMail && (
        <dl className={styles.mailHead}>
          {entry.from && (
            <>
              <dt>{labels.from}</dt>
              <dd dir="auto">{entry.from}</dd>
            </>
          )}
          {entry.subject && (
            <>
              <dt>{labels.subject}</dt>
              <dd dir="auto">{entry.subject}</dd>
            </>
          )}
        </dl>
      )}
      {entry.delivery === 'contextual' && entry.leadTemplate && (
        <div className={`${styles.bubble} ${styles.lead}`} dir="auto">
          {entry.leadTemplate}
        </div>
      )}
      {entry.delivery === 'contextual' && entry.followsLabel && <p className={styles.follows}>{entry.followsLabel}</p>}
      {/* The message as it lands. `dir="auto"` so a Hebrew message inside an English card still lays
          out right-to-left, and the text starts at the bubble's own edge in either direction. */}
      <div className={styles.bubble} dir="auto">
        {entry.delivery === 'wrapped' ? (
          <>
            {entry.frameBefore && <span className={styles.frame}>{entry.frameBefore}</span>}
            <span className={styles.words}>{entry.body}</span>
            {entry.frameAfter && <span className={styles.frame}>{entry.frameAfter}</span>}
          </>
        ) : (
          entry.body
        )}
      </div>
      {entry.files && entry.files.length > 0 && (
        <div className={styles.files}>
          <span className={styles.filesLabel}>{labels.files}</span>
          <ul className={styles.fileList}>
            {entry.files.map((file) => (
              <li key={file} className={styles.file} dir="ltr">
                <Paperclip size={12} strokeWidth={2} aria-hidden="true" />
                {file}
              </li>
            ))}
          </ul>
        </div>
      )}
      {entry.footnote && <p className={styles.footnote}>{entry.footnote}</p>}
    </Card>
  );
}
